import { motion } from 'framer-motion';
import { Check, Plus, X } from 'lucide-react';
import React, { useCallback, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';

import {
  DataCollectionAnswer,
  DataCollectionQuestion,
  DataCollectionSubQuestion,
  ObjectArrayConfig,
} from './types';

// ---------------------------------------------------------------------------
// Animation Config (consistent with QualificationScreen)
// ---------------------------------------------------------------------------

const springConfig = { type: 'spring' as const, stiffness: 500, damping: 30 };

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface DataCollectionScreenProps {
  questions: DataCollectionQuestion[];
  answers: Record<string, DataCollectionAnswer>;
  onAnswerChange: (questionId: string, value: string | undefined) => void;
  onAskedChange: (questionId: string, asked: boolean) => void;
  onArrayChange: (questionId: string, arrayValue: Record<string, string>[]) => void;
  /** Field ID to highlight (for deep-link navigation from End Intake) */
  highlightedFieldId?: string | null;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface YesNoButtonsProps {
  value: string | undefined;
  onChange: (value: string) => void;
  includeUnknown?: boolean;
  size?: 'normal' | 'small';
}

const YesNoButtons: React.FC<YesNoButtonsProps> = ({
  value,
  onChange,
  includeUnknown = false,
  size = 'normal',
}) => {
  const isSmall = size === 'small';

  return (
    <div className={cn('flex gap-1.5', isSmall ? 'gap-1' : 'gap-1.5')}>
      <motion.button
        type="button"
        onClick={() => onChange('yes')}
        className={cn(
          'rounded-lg font-black uppercase tracking-wide transition-colors duration-100',
          isSmall ? 'flex-1 h-8 text-[10px]' : 'flex-1 h-9 text-xs',
          value === 'yes'
            ? 'bg-black text-white'
            : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={springConfig}
      >
        Yes
      </motion.button>
      <motion.button
        type="button"
        onClick={() => onChange('no')}
        className={cn(
          'rounded-lg font-black uppercase tracking-wide transition-colors duration-100',
          isSmall ? 'flex-1 h-8 text-[10px]' : 'flex-1 h-9 text-xs',
          value === 'no'
            ? 'bg-zinc-700 text-white'
            : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'
        )}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        transition={springConfig}
      >
        No
      </motion.button>
      {includeUnknown && (
        <motion.button
          type="button"
          onClick={() => onChange('unknown')}
          className={cn(
            'rounded-lg font-black transition-colors duration-100',
            isSmall ? 'w-10 h-8 text-[10px]' : 'w-12 h-9 text-xs',
            value === 'unknown'
              ? 'bg-amber-500 text-white'
              : 'bg-zinc-100 text-amber-600 hover:bg-amber-50'
          )}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          transition={springConfig}
        >
          ?
        </motion.button>
      )}
    </div>
  );
};

interface TextInputProps {
  value: string | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  multiline?: boolean;
}

/**
 * TextInput with focus-gated local buffer.
 * When NOT focused: shows prop value (always correct after save/refresh).
 * When focused: shows localValue for smooth typing.
 * On blur: propagates to parent, reverts to prop value display.
 */
const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  placeholder,
  multiline = false,
}) => {
  const propValue = value ?? '';
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(propValue);
  const localRef = React.useRef(localValue);
  localRef.current = localValue;

  const handleFocus = useCallback(() => {
    setLocalValue(propValue); // sync before editing
    setIsFocused(true);
  }, [propValue]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    if (localRef.current !== propValue) {
      onChange(localRef.current);
    }
  }, [onChange, propValue]);

  const displayValue = isFocused ? localValue : propValue;

  const baseClass =
    'w-full px-3 py-2 text-xs font-semibold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 focus:border-transparent transition-colors placeholder:text-zinc-400 placeholder:font-normal';

  if (multiline) {
    return (
      <textarea
        value={displayValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={2}
        className={cn(baseClass, 'resize-none')}
      />
    );
  }

  return (
    <input
      type="text"
      value={displayValue}
      onChange={(e) => setLocalValue(e.target.value)}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={cn(baseClass, 'h-9')}
    />
  );
};

interface DateInputProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

const DateInput: React.FC<DateInputProps> = ({ value, onChange }) => {
  // Date inputs: propagate on change (no typing lag for date pickers)
  // but use local state to avoid parent re-render loops
  const [localValue, setLocalValue] = useState(value ?? '');

  React.useEffect(() => {
    setLocalValue(value ?? '');
  }, [value]);

  return (
    <input
      type="date"
      value={localValue}
      onChange={(e) => {
        setLocalValue(e.target.value);
        onChange(e.target.value); // dates commit on picker selection, safe to propagate immediately
      }}
      className="w-full h-9 px-3 text-xs font-semibold text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-inset focus:ring-zinc-900 focus:border-transparent transition-colors"
    />
  );
};

interface AskClientPromptProps {
  prompt: string;
  asked: boolean;
  onAskedChange: (asked: boolean) => void;
}

const AskClientPrompt: React.FC<AskClientPromptProps> = ({
  prompt,
  asked,
  onAskedChange,
}) => (
  <div
    className={cn(
      'rounded-xl py-2 px-3 flex items-center justify-between gap-3 transition-colors duration-150',
      asked ? 'bg-zinc-100' : 'bg-zinc-900'
    )}
  >
    <div className="min-w-0 flex-1">
      <div
        className={cn(
          'text-[8px] font-black uppercase tracking-widest mb-0.5',
          asked ? 'text-zinc-400' : 'text-zinc-500'
        )}
      >
        Ask Client
      </div>
      <p
        className={cn(
          'font-semibold text-xs leading-snug',
          asked ? 'text-zinc-500' : 'text-white'
        )}
      >
        {prompt}
      </p>
    </div>
    <motion.button
      type="button"
      onClick={() => onAskedChange(!asked)}
      className={cn(
        'h-7 px-3 text-[10px] font-black uppercase tracking-wider rounded-lg flex items-center gap-1 shrink-0 transition-colors duration-100',
        asked
          ? 'bg-zinc-200 text-zinc-500 hover:bg-zinc-300'
          : 'bg-white text-black hover:bg-zinc-200'
      )}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.96 }}
      transition={springConfig}
    >
      {asked ? (
        <>
          <Check className="w-3 h-3" /> Asked
        </>
      ) : (
        'Done'
      )}
    </motion.button>
  </div>
);

// ---------------------------------------------------------------------------
// Object Array Input (for multiple providers, etc.)
// ---------------------------------------------------------------------------

interface ObjectArrayInputProps {
  config: ObjectArrayConfig;
  value: Record<string, string>[];
  onChange: (value: Record<string, string>[]) => void;
  /** Whether "none" has been confirmed (no items expected) */
  isNoneConfirmed?: boolean;
  /** Callback to toggle the "none" state */
  onNoneToggle?: () => void;
}

const ObjectArrayInput: React.FC<ObjectArrayInputProps> = ({
  config,
  value,
  onChange,
  isNoneConfirmed,
  onNoneToggle,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const resetDraft = useCallback(() => {
    setDraft({});
    setIsAdding(false);
    setEditingIndex(null);
  }, []);

  const handleAddClick = useCallback(() => {
    setDraft({});
    setIsAdding(true);
    setEditingIndex(null);
  }, []);

  const handleEditClick = useCallback((index: number) => {
    setDraft({ ...value[index] });
    setEditingIndex(index);
    setIsAdding(false);
  }, [value]);

  const handleSave = useCallback(() => {
    // Validate required fields
    const hasRequired = config.fields
      .filter((f) => f.required)
      .every((f) => draft[f.key]?.trim());

    if (!hasRequired) return;

    if (editingIndex !== null) {
      const newValue = [...value];
      newValue[editingIndex] = draft;
      onChange(newValue);
    } else {
      onChange([...value, draft]);
    }
    resetDraft();
  }, [config.fields, draft, editingIndex, onChange, resetDraft, value]);

  const handleDelete = useCallback((index: number) => {
    onChange(value.filter((_, i) => i !== index));
  }, [onChange, value]);

  const formatDisplay = (item: Record<string, string>) => {
    if (!config.displayFormat) {
      return config.fields
        .filter((f) => item[f.key])
        .map((f) => item[f.key])
        .join(' · ');
    }
    let display = config.displayFormat;
    config.fields.forEach((f) => {
      display = display.replace(`{${f.key}}`, item[f.key] || '');
    });
    return display.replace(/\s*·\s*·\s*/g, ' · ').replace(/^\s*·\s*|\s*·\s*$/g, '').trim();
  };

  // Format date range for display (e.g., "Jan 15 - Feb 20" or "Jan 15 - present")
  const formatDateRange = (item: Record<string, string>) => {
    const startDate = item.start_date;
    const endDate = item.end_date;

    if (!startDate && !endDate) return null;

    const formatDate = (dateStr: string) => {
      const date = new Date(`${dateStr}T00:00:00`);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    if (startDate && endDate) {
      return `${formatDate(startDate)} – ${formatDate(endDate)}`;
    }
    if (startDate) {
      return `${formatDate(startDate)} – present`;
    }
    if (endDate) {
      return `until ${formatDate(endDate)}`;
    }
    return null;
  };

  const isFormOpen = isAdding || editingIndex !== null;

  return (
    <div className="space-y-2">
      {/* Existing items as compact pills */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((item, index) => {
            const dateRange = formatDateRange(item);
            const details = item.details?.trim();
            return (
              <motion.div
                key={index}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className={cn(
                  'group flex items-center gap-1.5 pl-2.5 pr-1 py-1 rounded-lg text-xs transition-colors cursor-pointer',
                  editingIndex === index
                    ? 'bg-zinc-900 text-white'
                    : 'bg-zinc-100 text-zinc-700 hover:bg-zinc-200'
                )}
                onClick={() => handleEditClick(index)}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <span className="font-semibold truncate max-w-[140px]">{formatDisplay(item)}</span>
                  {dateRange && (
                    <span className={cn(
                      'text-[10px] shrink-0',
                      editingIndex === index ? 'text-zinc-400' : 'text-zinc-500'
                    )}>
                      {dateRange}
                    </span>
                  )}
                  {details && (
                    <span className={cn(
                      'text-[10px] truncate max-w-[100px]',
                      editingIndex === index ? 'text-zinc-400' : 'text-zinc-500'
                    )}>
                      · {details}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(index);
                  }}
                  className={cn(
                    'p-0.5 rounded transition-colors',
                    editingIndex === index
                      ? 'hover:bg-zinc-700 text-zinc-500 hover:text-zinc-300'
                      : 'hover:bg-zinc-300 text-zinc-400 hover:text-zinc-600'
                  )}
                >
                  <X className="w-3 h-3" />
                </button>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Add/Edit form - CSS grid for smooth height animation */}
      <div
        className="grid transition-[grid-template-rows] duration-200 ease-out"
        style={{ gridTemplateRows: isFormOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          {isFormOpen && (
            <div className="bg-zinc-50 rounded-xl p-3 space-y-2 border border-zinc-200">
              <div className="grid grid-cols-2 gap-2">
                {config.fields.map((field) => {
                  // First text field and textareas span full width
                  const isFullWidth = field.type === 'textarea' || (field.type === 'text' && field.key === config.fields[0].key);
                  const fieldId = `data-collection-${field.key}`;
                  return (
                    <div key={field.key} className={isFullWidth ? 'col-span-2' : ''}>
                      <label htmlFor={fieldId} className="text-[9px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-0.5">
                        {field.label}
                        {field.required && <span className="text-rose-400">*</span>}
                      </label>
                      {field.type === 'date' ? (
                        <input
                          id={fieldId}
                          type="date"
                          value={draft[field.key] || ''}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          className="w-full h-8 px-2 text-xs font-semibold text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent"
                        />
                      ) : field.type === 'textarea' ? (
                        <textarea
                          id={fieldId}
                          value={draft[field.key] || ''}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          placeholder={field.placeholder}
                          rows={2}
                          className="w-full px-2 py-1.5 text-xs font-semibold text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent placeholder:text-zinc-400 placeholder:font-normal resize-none"
                        />
                      ) : (
                        <input
                          id={fieldId}
                          type="text"
                          value={draft[field.key] || ''}
                          onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                          placeholder={field.placeholder}
                          className="w-full h-8 px-2 text-xs font-semibold text-zinc-900 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-zinc-900 focus:border-transparent placeholder:text-zinc-400 placeholder:font-normal"
                        />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex gap-1.5 pt-1">
                <motion.button
                  type="button"
                  onClick={handleSave}
                  className="flex-1 h-8 text-[10px] font-black uppercase tracking-wider rounded-lg bg-zinc-900 text-white hover:bg-zinc-800 transition-colors"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {editingIndex !== null ? 'Update' : 'Save'}
                </motion.button>
                <motion.button
                  type="button"
                  onClick={resetDraft}
                  className="px-3 h-8 text-[10px] font-black uppercase tracking-wider rounded-lg bg-zinc-200 text-zinc-600 hover:bg-zinc-300 transition-colors"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add button / None toggle */}
      {!isFormOpen && !isNoneConfirmed && (
        <div className="flex gap-1.5">
          <motion.button
            type="button"
            onClick={handleAddClick}
            className="flex items-center gap-1.5 h-8 px-3 text-[10px] font-black uppercase tracking-wider rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-200 transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            <Plus className="w-3 h-3" />
            {config.addButtonText || 'Add Item'}
          </motion.button>
          {value.length === 0 && onNoneToggle && (
            <motion.button
              type="button"
              onClick={onNoneToggle}
              className="flex items-center gap-1.5 h-8 px-3 text-[10px] font-black uppercase tracking-wider rounded-lg bg-zinc-100 text-zinc-500 hover:bg-zinc-200 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.97 }}
            >
              None
            </motion.button>
          )}
        </div>
      )}

      {/* None confirmed state */}
      {!isFormOpen && isNoneConfirmed && value.length === 0 && (
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-zinc-500">No providers</span>
          <motion.button
            type="button"
            onClick={onNoneToggle}
            className="text-[10px] font-bold text-zinc-400 hover:text-zinc-600 underline transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            Undo
          </motion.button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sub-question renderer
// ---------------------------------------------------------------------------

interface SubQuestionRowProps {
  subQuestion: DataCollectionSubQuestion;
  answer: DataCollectionAnswer | undefined;
  onAnswerChange: (value: string | undefined) => void;
  onAskedChange: (asked: boolean) => void;
  /** Whether this specific sub-question is highlighted */
  isHighlighted?: boolean;
}

const SubQuestionRow: React.FC<SubQuestionRowProps> = ({
  subQuestion,
  answer,
  onAnswerChange,
  onAskedChange,
  isHighlighted,
}) => {
  const renderInput = () => {
    switch (subQuestion.inputType) {
      case 'yes_no':
        return (
          <YesNoButtons
            value={answer?.value}
            onChange={onAnswerChange}
            size="small"
          />
        );
      case 'yes_no_unknown':
        return (
          <YesNoButtons
            value={answer?.value}
            onChange={onAnswerChange}
            includeUnknown
            size="small"
          />
        );
      case 'text':
        return (
          <TextInput
            value={answer?.value}
            onChange={onAnswerChange}
            placeholder={subQuestion.placeholder}
          />
        );
      case 'date':
        return <DateInput value={answer?.value} onChange={onAnswerChange} />;
      case 'ask_client':
        return (
          <AskClientPrompt
            prompt={subQuestion.askClientPrompt ?? subQuestion.text}
            asked={answer?.asked ?? false}
            onAskedChange={onAskedChange}
          />
        );
      default:
        return null;
    }
  };

  // For ask_client, render full width prompt with proper spacing
  if (subQuestion.inputType === 'ask_client') {
    return (
      <div
        className={cn(
          "rounded-xl mt-2",
          isHighlighted && "ring-2 ring-rose-300 animate-highlight-blink"
        )}
      >
        {renderInput()}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-xl p-2 -m-2",
        isHighlighted && "ring-2 ring-rose-300 animate-highlight-blink"
      )}
    >
      <div className="space-y-1">
        <label htmlFor={subQuestion.id} className="text-[10px] font-bold text-zinc-500 uppercase tracking-wider flex items-center gap-1">
          {subQuestion.text}
          {subQuestion.required && <span className="text-rose-400">*</span>}
        </label>
        {renderInput()}
        {subQuestion.helperText && answer?.value === 'yes' && (
          <p className="text-[10px] font-medium text-zinc-400 mt-0.5">{subQuestion.helperText}</p>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Question Card
// ---------------------------------------------------------------------------

interface QuestionCardProps {
  question: DataCollectionQuestion;
  answers: Record<string, DataCollectionAnswer>;
  onAnswerChange: (questionId: string, value: string | undefined) => void;
  onAskedChange: (questionId: string, asked: boolean) => void;
  onArrayChange: (questionId: string, arrayValue: Record<string, string>[]) => void;
  /** Field ID to highlight (for deep-link navigation from End Intake) */
  highlightedFieldId?: string | null;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  answers,
  onAnswerChange,
  onAskedChange,
  onArrayChange,
  highlightedFieldId,
}) => {
  const mainAnswer = answers[question.id];
  const isCardHighlighted = highlightedFieldId === question.id;

  // Determine which sub-questions to show based on their conditions
  const visibleSubQuestions = useMemo(() => {
    if (!question.subQuestions) return [];

    return question.subQuestions.filter((sub) => {
      if (!sub.showCondition) return true; // Always show if no condition

      // Check if it's a parentAnswer condition (legacy format)
      if ('parentAnswer' in sub.showCondition) {
        if (sub.showCondition.parentAnswer === 'any') {
          return mainAnswer?.value !== undefined;
        }
        return mainAnswer?.value === sub.showCondition.parentAnswer;
      }

      // It's a questionId condition (new nested format)
      // Look up the answer for the specified question/sub-question
      const targetAnswer = answers[sub.showCondition.questionId];
      return targetAnswer?.value === sub.showCondition.answer;
    });
  }, [question.subQuestions, mainAnswer?.value, answers]);

  // For group type, always show all sub-questions
  const subQuestionsToRender =
    question.inputType === 'group'
      ? question.subQuestions ?? []
      : visibleSubQuestions;

  // Check if card is "complete" (has main answer + visible sub-questions answered)
  const isComplete = useMemo(() => {
    if (question.inputType === 'group') {
      // For groups, check if required fields are filled
      return (question.subQuestions ?? []).every((sub) => {
        if (!sub.required) return true;
        const subAnswer = answers[sub.id];
        return subAnswer?.value !== undefined && subAnswer.value !== '';
      });
    }

    if (question.inputType === 'ask_client') {
      return mainAnswer?.asked === true;
    }

    // For object_array, check if at least one item exists OR "none" confirmed
    if (question.inputType === 'object_array') {
      return (mainAnswer?.arrayValue?.length ?? 0) > 0 || mainAnswer?.value === 'none';
    }

    // For regular questions, need a main answer
    if (!mainAnswer?.value) return false;

    // Check visible sub-questions
    return visibleSubQuestions.every((sub) => {
      const subAnswer = answers[sub.id];
      if (sub.inputType === 'ask_client') {
        return subAnswer?.asked === true;
      }
      if (sub.required) {
        return subAnswer?.value !== undefined && subAnswer.value !== '';
      }
      return true;
    });
  }, [question, mainAnswer, answers, visibleSubQuestions]);

  const renderMainInput = () => {
    switch (question.inputType) {
      case 'yes_no':
        return (
          <YesNoButtons
            value={mainAnswer?.value}
            onChange={(v) => onAnswerChange(question.id, v)}
          />
        );
      case 'yes_no_unknown':
        return (
          <YesNoButtons
            value={mainAnswer?.value}
            onChange={(v) => onAnswerChange(question.id, v)}
            includeUnknown
          />
        );
      case 'text':
        return (
          <TextInput
            value={mainAnswer?.value}
            onChange={(v) => onAnswerChange(question.id, v)}
            placeholder={question.placeholder}
            multiline
          />
        );
      case 'date':
        return (
          <DateInput
            value={mainAnswer?.value}
            onChange={(v) => onAnswerChange(question.id, v)}
          />
        );
      case 'ask_client':
        return (
          <AskClientPrompt
            prompt={question.askClientPrompt ?? question.text}
            asked={mainAnswer?.asked ?? false}
            onAskedChange={(asked) => onAskedChange(question.id, asked)}
          />
        );
      case 'group':
        // Group type has no main input, only sub-questions
        return null;
      case 'object_array':
        if (!question.objectArrayConfig) return null;
        return (
          <ObjectArrayInput
            config={question.objectArrayConfig}
            value={mainAnswer?.arrayValue ?? []}
            onChange={(arrayValue) => {
              onArrayChange(question.id, arrayValue);
              // Clear "none" when items are added
              if (arrayValue.length > 0 && mainAnswer?.value === 'none') {
                onAnswerChange(question.id, undefined);
              }
            }}
            isNoneConfirmed={mainAnswer?.value === 'none'}
            onNoneToggle={() => {
              onAnswerChange(
                question.id,
                mainAnswer?.value === 'none' ? undefined : 'none'
              );
            }}
          />
        );
      default:
        return null;
    }
  };

  // Determine border style
  const getBorderStyle = () => {
    if (isCardHighlighted) return 'border-rose-400 ring-2 ring-rose-300 animate-highlight-blink';
    if (isComplete) return 'border-emerald-200';
    return 'border-zinc-200';
  };

  return (
    <div
      className={cn(
        'bg-white rounded-[20px] border transition-colors duration-150',
        getBorderStyle()
      )}
    >
      <div className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <h3
            className={cn(
              'font-bold text-sm leading-tight',
              isComplete ? 'text-zinc-500' : 'text-zinc-900'
            )}
          >
            {question.text}
          </h3>
          {isComplete && (
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="shrink-0 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center"
            >
              <Check className="w-3 h-3 text-white" />
            </motion.div>
          )}
        </div>

        {/* Main Input */}
        {question.inputType !== 'group' && (
          <div>
            {renderMainInput()}
            {question.helperText && mainAnswer?.value === 'yes' && (
              <p className="text-[10px] font-bold text-zinc-600 mt-1">{question.helperText}</p>
            )}
          </div>
        )}

        {/* Sub-questions - CSS grid for smooth height animation */}
        <div
          className="grid transition-[grid-template-rows] duration-200 ease-out"
          style={{ gridTemplateRows: subQuestionsToRender.length > 0 ? '1fr' : '0fr' }}
        >
          <div className="overflow-hidden">
            {subQuestionsToRender.length > 0 && (
              <div className="space-y-3 pt-2 border-t border-zinc-100">
                {subQuestionsToRender.map((sub) => (
                  <SubQuestionRow
                    key={sub.id}
                    subQuestion={sub}
                    answer={answers[sub.id]}
                    onAnswerChange={(v) => onAnswerChange(sub.id, v)}
                    onAskedChange={(asked) => onAskedChange(sub.id, asked)}
                    isHighlighted={highlightedFieldId === sub.id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section Header
// ---------------------------------------------------------------------------

interface SectionHeaderProps {
  title: string;
  subtitle: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({ title, subtitle }) => (
  <div className="mb-2">
    <h2 className="font-black text-sm text-zinc-900">{title}</h2>
    <span className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">
      {subtitle}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const DataCollectionScreen: React.FC<DataCollectionScreenProps> = ({
  questions,
  answers,
  onAnswerChange,
  onAskedChange,
  onArrayChange,
  highlightedFieldId,
}) => {
  // Split questions by priority
  const p1Questions = useMemo(
    () => questions.filter((q) => q.priority === 'P1'),
    [questions]
  );
  const p2Questions = useMemo(
    () => questions.filter((q) => q.priority === 'P2'),
    [questions]
  );

  // Split questions into fixed left/right columns
  // Uses explicit column property when available, otherwise alternates by index
  const p1LeftColumn = useMemo(
    () => p1Questions.filter((q, i) => q.column === 'left' || (!q.column && i % 2 === 0)),
    [p1Questions]
  );
  const p1RightColumn = useMemo(
    () => p1Questions.filter((q, i) => q.column === 'right' || (!q.column && i % 2 === 1)),
    [p1Questions]
  );
  const p2LeftColumn = useMemo(
    () => p2Questions.filter((q, i) => q.column === 'left' || (!q.column && i % 2 === 0)),
    [p2Questions]
  );
  const p2RightColumn = useMemo(
    () => p2Questions.filter((q, i) => q.column === 'right' || (!q.column && i % 2 === 1)),
    [p2Questions]
  );

  // Helper to render a question card
  const renderQuestionCard = (question: DataCollectionQuestion) => (
    <div key={question.id} className="mb-2">
      <QuestionCard
        question={question}
        answers={answers}
        onAnswerChange={onAnswerChange}
        onAskedChange={onAskedChange}
        onArrayChange={onArrayChange}
        highlightedFieldId={highlightedFieldId}
      />
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="max-w-4xl mx-auto space-y-4">
          {/* P1 Section - Required Information */}
          <div>
            <SectionHeader
              title="Required Information"
              subtitle="Must collect"
            />
            <div className="grid grid-cols-2 gap-2 items-start">
              {/* Left column */}
              <div className="space-y-2">
                {p1LeftColumn.map(renderQuestionCard)}
              </div>
              {/* Right column */}
              <div className="space-y-2">
                {p1RightColumn.map(renderQuestionCard)}
              </div>
            </div>
          </div>

          {/* P2 Section - Additional Details */}
          {p2Questions.length > 0 && (
            <div>
              <SectionHeader
                title="Additional Details"
                subtitle="Nice to have"
              />
              <div className="grid grid-cols-2 gap-2 items-start">
                {/* Left column */}
                <div className="space-y-2">
                  {p2LeftColumn.map(renderQuestionCard)}
                </div>
                {/* Right column */}
                <div className="space-y-2">
                  {p2RightColumn.map(renderQuestionCard)}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataCollectionScreen;
